import { useCallback, useSyncExternalStore } from "react";

/**
 * The board's URL state (ADR-055 D1, amending ADR-023): `/pm` mounts the board, and everything below it is query
 * params, no router library. `arc_pm_ms=<id>` is the open micro-scenario (absent ⇒ the landing page);
 * `arc_pm_view=drehbuch|tasks|meeting|verlauf` its view (absent ⇒ the Drehbuch); `arc_pm_task=<id>` a task open in
 * its details dialog over that view (phase 6), so a task can be linked to.
 *
 * Pure parse/serialize below, a thin `useSyncExternalStore` shell on top — the same split as the facility navigator,
 * including its custom event: `pushState` does not fire `popstate`, so every write announces itself.
 */
export const PM_VIEWS = ["drehbuch", "tasks", "meeting", "verlauf"] as const;
export type PmView = (typeof PM_VIEWS)[number];

export interface PmNavState {
  msId: number | null;
  view: PmView;
  taskId: number | null;
}

const NAV_EVENT = "arc:pmnavchange";

/** Unknown or malformed values fall back to the landing page / the Drehbuch — a stale link never lands on a blank. */
export function parsePmNav(search: string): PmNavState {
  const q = new URLSearchParams(search);
  const rawMs = q.get("arc_pm_ms");
  const id = (raw: string | null) => (raw !== null && /^[1-9][0-9]{0,9}$/.test(raw) ? Number(raw) : null);
  const msId = id(rawMs);
  const rawView = q.get("arc_pm_view");
  const view = (PM_VIEWS as readonly string[]).includes(rawView ?? "") ? (rawView as PmView) : "drehbuch";
  // A task only means something inside its MS.
  return { msId, view, taskId: msId === null ? null : id(q.get("arc_pm_task")) };
}

/** The query for a state; other params on the URL are kept (an auth callback, a future filter). */
export function serializePmNav(search: string, next: PmNavState): string {
  const q = new URLSearchParams(search);
  q.delete("arc_pm_ms");
  q.delete("arc_pm_view");
  q.delete("arc_pm_task");
  if (next.msId !== null) {
    q.set("arc_pm_ms", String(next.msId));
    if (next.view !== "drehbuch") q.set("arc_pm_view", next.view);
    if (next.taskId !== null) q.set("arc_pm_task", String(next.taskId));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

function subscribe(cb: () => void): () => void {
  window.addEventListener("popstate", cb);
  window.addEventListener(NAV_EVENT, cb);
  return () => {
    window.removeEventListener("popstate", cb);
    window.removeEventListener(NAV_EVENT, cb);
  };
}
const getSnapshot = () => window.location.search;

export function usePmNav() {
  const search = useSyncExternalStore(subscribe, getSnapshot);
  const state = parsePmNav(search);
  const go = useCallback((next: PmNavState) => {
    const url = `${window.location.pathname}${serializePmNav(window.location.search, next)}${window.location.hash}`;
    window.history.pushState(null, "", url);
    window.dispatchEvent(new Event(NAV_EVENT));
  }, []);
  return {
    ...state,
    openMs: useCallback((msId: number) => go({ msId, view: "drehbuch", taskId: null }), [go]),
    openView: useCallback((msId: number, view: PmView) => go({ msId, view, taskId: null }), [go]),
    openTask: useCallback((msId: number, view: PmView, taskId: number) => go({ msId, view, taskId }), [go]),
    closeTask: useCallback((msId: number, view: PmView) => go({ msId, view, taskId: null }), [go]),
    backToLanding: useCallback(() => go({ msId: null, view: "drehbuch", taskId: null }), [go]),
  };
}

/** Whether this document is the board — read ONCE at boot from the pathname (D1: one pathname switch). */
export function isPmBoardPath(pathname: string): boolean {
  return pathname === "/pm" || pathname === "/pm/";
}
